// component/Geospatial/Facilitydetailpanel.jsx
import React from 'react';
import { TYPE_COLORS } from './mockData/GeospatialData';

const FUNCTIONAL_STATUS_COLORS = {
  fully_functional: '#22c55e',
  partially_functional: '#f59e0b',
  non_functional: '#ef4444',
  under_construction: '#3b82f6',
  decommissioned: '#9ca3af',
  active: '#22c55e',
  inactive: '#ef4444',
  seasonal: '#f59e0b',
  under_rehabilitation: '#3b82f6',
};

const humanize = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StatusBadge = ({ status }) => {
  const key = String(status ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  const color = FUNCTIONAL_STATUS_COLORS[key] || '#6b7280';
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color }}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {humanize(status) || 'Unknown'}
    </span>
  );
};

const DetailRow = ({ label, value, bold }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-gray-100 gap-2">
    <span className="text-gray-500 text-[13px] font-medium shrink-0">{label}</span>
    <span className={`text-[13px] text-right ${bold ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
      {value || '-'}
    </span>
  </div>
);

const TypeBadge = ({ type }) => {
  const label = humanize(type) || 'Unknown';
  const color = TYPE_COLORS[label] || '#6b7280';
  return (
    <span
      className="inline-block rounded-md text-xs font-semibold px-2.5 py-0.5 tracking-wide"
      style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
    >
      {label}
    </span>
  );
};

// Shown when the map draws the record somewhere other than its recorded GPS
// (see displayPoints.js)
const LocationNote = ({ facility }) => {
  const state = facility.state || 'its state';
  if (facility.locationAccuracy === 'outside-state') {
    return (
      <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
        The recorded GPS is outside {state}, so this marker is shown at an approximate spot in {state}.
        Correct the coordinates on the record to pin its exact LGA / ward location.
      </p>
    );
  }
  if (facility.locationAccuracy === 'no-gps') {
    return (
      <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
        No GPS recorded — shown at an approximate spot in {state}.
      </p>
    );
  }
  return null;
};

const EmptyState = () => (
  <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 p-6 text-center text-gray-400">
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
        fill="#d1d5db"
      />
    </svg>
    <p className="m-0 text-sm leading-relaxed">Click a point on the map to view details</p>
  </div>
);

export default function FacilityDetailPanel({ facility, onClose }) {
  if (!facility) return <EmptyState />;

  const type = facility.type ?? facility.facilityType ?? '';
  const status = facility.status ?? facility.functionalStatus ?? '';
  const isMarketRecord = facility.kind === 'market';
  const isMarket = isMarketRecord || ['Market', 'Live Animal Market'].includes(type);
  const isClinic = ['Veterinary Clinic', 'Veterinary Hospital'].includes(type);
  const isRanch = type === 'Ranch';
  const isProcessing = ['Processing', 'Feed Mill', 'Cold Storage'].includes(type);
  const marketType = facility.market_type_label ?? facility.marketTypeLabel ?? facility.market_type ?? '';
  const marketSchedule = facility.market_schedule_label ?? facility.marketScheduleLabel ?? facility.market_schedule ?? '';
  const sourceLabel = isMarketRecord ? 'Market Registry' : 'Facility Registry';

  return (
    <div className="flex min-h-[220px] flex-col lg:h-full">
      <div className="flex justify-between items-start px-5 pt-5 pb-3.5 border-b border-gray-100">
        <div className="flex-1 mr-2">
          <h2 className="m-0 mb-1.5 text-base font-bold text-gray-900 leading-snug">
            {facility.name || 'Facility'}
          </h2>
          <TypeBadge type={type} />
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
            {sourceLabel}
          </p>
        </div>
        <button
          onClick={onClose}
          className="bg-transparent border-none cursor-pointer text-gray-400 text-lg leading-none shrink-0 hover:text-gray-600 transition-colors p-0.5"
          aria-label="Close"
        >
          x
        </button>
      </div>

      <div className="px-5 flex-1 overflow-y-auto">
        <DetailRow label="State" value={facility.state} />
        <DetailRow label="LGA" value={facility.lga} />
        <DetailRow label="Ward" value={facility.ward} />
        {(!isMarketRecord || facility.capacity) && <DetailRow label="Capacity" value={facility.capacity} />}

        <div className="flex justify-between items-center py-2.5 border-b border-gray-100">
          <span className="text-gray-500 text-[13px] font-medium">Status</span>
          <StatusBadge status={status} />
        </div>

        {isMarket && (
          <>
            <DetailRow label="Market Type" value={marketType} />
            <DetailRow label="Market Schedule" value={marketSchedule} />
            {facility.tradingDays && <DetailRow label="Trading Days" value={facility.tradingDays} />}
          </>
        )}
        {isClinic && facility.services && <DetailRow label="Services" value={facility.services} bold />}
        {isRanch && facility.breeds && <DetailRow label="Breeds" value={facility.breeds} />}
        {isProcessing && facility.certifications && <DetailRow label="Certifications" value={facility.certifications} />}

        <DetailRow label="Latitude" value={facility.lat != null ? `${facility.lat}` : '-'} />
        <DetailRow label="Longitude" value={facility.lng != null ? `${facility.lng}` : '-'} />
        <LocationNote facility={facility} />

        {facility.description && (
          <div className="pt-3.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 m-0 mb-1.5">About</p>
            <p className="m-0 text-[13px] text-gray-600 leading-relaxed">{facility.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
